import type { SearchParams } from '@popularKeyword/common-hooks';

import api from '@/utils/api/axiosInstance';

import { MonthlyKeywordModel, RisingKeywordModel } from '@popularKeyword/common-types';
import ErrorReportStore from '@/stores/ErrorReportStore';

const URL = {
  RISING_KEYWORD: 'MW/api/seo/top',
  MONTHLY_KEYWORD: 'MW/api/seo/category',
} as const;

const KEYWORD_COUNT = {
  MAIN: 40,
  DETAIL: 400,
} as const;

const getRisingKeyword = async (): Promise<RisingKeywordModel> => {
  try {
    const { data } = await api.get(`${URL.RISING_KEYWORD}?limit=${KEYWORD_COUNT.MAIN}&domain=pc`);
    return data;
  } catch (e) {
    pushErrorReport(e, 'getRisingKeyword');
    throw e;
  }
};

const getMonthlyKeyword = async (currentDate?: string): Promise<MonthlyKeywordModel> => {
  try {
    if (!currentDate) {
      const { data } = await api.get(`${URL.MONTHLY_KEYWORD}?offset=${0}&limit=${KEYWORD_COUNT.MAIN}&domain=pc`);
      return data;
    }

    const { data } = await api.get(
      `${URL.MONTHLY_KEYWORD}/${currentDate}?offset=${0}&limit=${KEYWORD_COUNT.MAIN}&domain=pc`,
    );
    return data;
  } catch (e) {
    pushErrorReport(e, 'getMonthlyKeyword');
    throw e;
  }
};

type getDetail = {
  currentPage: number;
} & Pick<SearchParams, 'date' | 'category' | 'rising'>;

const getDetail = async ({ currentPage, date, category, rising }: getDetail) => {
  try {
    if (!date) {
      const path =
        rising === 'true'
          ? `MW/api/seo/top?offset=${currentPage}&limit=${KEYWORD_COUNT.DETAIL}&domain=pc`
          : `MW/api/seo/category?category=${category}&offset=${currentPage}&limit=${KEYWORD_COUNT.DETAIL}&domain=pc`;
      const { data } = await api.get(path);
      return data;
    }

    const path =
      rising === 'true'
        ? `MW/api/seo/top?offset=${currentPage}&limit=${KEYWORD_COUNT.DETAIL}&domain=pc`
        : `MW/api/seo/category/${date}?category=${category}&offset=${currentPage}&limit=${KEYWORD_COUNT.DETAIL}&domain=pc`;
    const { data } = await api.get(path);
    return data;
  } catch (e) {
    pushErrorReport(e, 'getDetail');
    throw e;
  }
};

export type ErrorLog = {
  searchKeyword?: string;
  groupName: string;
  detail: {
    error: string;
    etc: string;
  };
  userAgent: string;
  poc: 'pc-popular';
};

const postErrorLog = async (logList: ErrorLog[]) => {
  try {
    const isProduction = process.env.NODE_ENV === 'production';
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (!isProduction || logList.length === 0) return;

    const logUrl = isDevelopment ? '/log' : 'https://apis.11st.co.kr/search-monitoring/log';

    await api({
      url: logUrl,
      method: 'POST',
      data: JSON.stringify(logList),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (e) {}
};

const pushErrorReport = (e, methodName: string) => {
  ErrorReportStore.updateErrorMethodName(e, methodName);
  ErrorReportStore.log({
    groupName: `popularKeyword > ${ErrorReportStore.getErrorMethodName(e)}`,
    detail: {
      error: ErrorReportStore.getErrorMessage(e),
      etc: window.location.href,
    },
    userAgent: ErrorReportStore.getUserAgent(),
    poc: ErrorReportStore.getPoc().APP_POPULAR,
  });
};

export { getRisingKeyword, getMonthlyKeyword, getDetail, postErrorLog };
